import type { ClaimDataType } from '@store/claim/types';
import type {
  ChangedFieldPathToken,
  ChangeTrackingSource,
} from '@typeDefinitions/changeTrackingTypes';

import { CHANGE_TRACKING_SOURCES } from '../../utils/changeTrackingRegistry';
import { createFormChangeTracker } from '../../utils/createFormChangeTracker';
import { findChangedForms } from '../../utils/findChangedForms';

const IGNORED_FIELDS = new Set([
  'agent',
  'comments',
  'customer',
  'loadNumber',
  'operationType',
  'occurredDateFormatted',
  'claimDateFormatted',
  'closedFormattedDate',
]);

const getPropertyPath = (path: readonly ChangedFieldPathToken[]): string[] =>
  path.flatMap((token) => (token.type === 'property' ? [token.key] : []));

const isEmptyFormValue = (value: unknown): boolean =>
  value === null || value === undefined || value === '';

const areDatesEqual = (before: string, after: string): boolean => {
  const beforeDate = new Date(before);
  const afterDate = new Date(after);

  return (
    !Number.isNaN(beforeDate.getTime()) &&
    !Number.isNaN(afterDate.getTime()) &&
    beforeDate.toDateString() === afterDate.toDateString()
  );
};

const areClaimScalarValuesEqual = (
  before: unknown,
  after: unknown,
  path: readonly ChangedFieldPathToken[],
): boolean => {
  if (Object.is(before, after)) return true;
  if (isEmptyFormValue(before) && isEmptyFormValue(after)) return true;

  const field = getPropertyPath(path).at(-1) ?? '';

  if (typeof before === 'number' && typeof after === 'string') {
    return after.trim() !== '' && Number(after) === before;
  }

  if (typeof before === 'string' && typeof after === 'number') {
    return before.trim() !== '' && Number(before) === after;
  }

  if (
    typeof before === 'string' &&
    typeof after === 'string' &&
    /date/i.test(field)
  ) {
    return areDatesEqual(before, after);
  }

  return false;
};

const getClaimFormSource = (
  path: readonly ChangedFieldPathToken[],
): ChangeTrackingSource | undefined => {
  const [field] = getPropertyPath(path);

  switch (field) {
    case 'status':
      return CHANGE_TRACKING_SOURCES.claimForm.status;
    case 'claimDate':
      return CHANGE_TRACKING_SOURCES.claimForm.claimDate;
    case 'occurredDate':
      return CHANGE_TRACKING_SOURCES.claimForm.occurredDate;
    case 'closedDate':
      return CHANGE_TRACKING_SOURCES.claimForm.closedDate;
    case 'commodity':
      return CHANGE_TRACKING_SOURCES.claimForm.commodity;
    case 'description':
      return CHANGE_TRACKING_SOURCES.claimForm.description;
    case 'referenceNumber':
      return CHANGE_TRACKING_SOURCES.claimForm.referenceNumber;
    case 'claimValue':
      return CHANGE_TRACKING_SOURCES.claimForm.claimValue;
    case 'carrierPaid':
      return CHANGE_TRACKING_SOURCES.claimForm.carrierPaid;
    case 'atgPaid':
      return CHANGE_TRACKING_SOURCES.claimForm.atgPaid;
    case 'insurancePaid':
      return CHANGE_TRACKING_SOURCES.claimForm.insurancePaid;
    case 'agentPaid':
      return CHANGE_TRACKING_SOURCES.claimForm.salvageValue;
    case 'amountDeclined':
      return CHANGE_TRACKING_SOURCES.claimForm.amountDeclined;
    default:
      return undefined;
  }
};

const findClaimFormChangedSources = (
  before: ClaimDataType,
  after: ClaimDataType,
): ChangeTrackingSource[] =>
  findChangedForms(before, after, {
    ignoredFields: IGNORED_FIELDS,
    areScalarValuesEqual: areClaimScalarValuesEqual,
    getForm: (change) => getClaimFormSource(change.path),
  });

const claimFormChangeTracker = createFormChangeTracker<
  ClaimDataType,
  ChangeTrackingSource
>({
  getIdentity: (claim) => claim.claimNumber ?? claim.orderId ?? claim.loadId,
  findChangedForms: findClaimFormChangedSources,
});

export const setClaimFormChangeBaseline = claimFormChangeTracker.setBaseline;
export const prepareClaimFormChangeSummary = claimFormChangeTracker.prepare;
